import { useMemo } from "react";
import { useQueries } from "@tanstack/react-query";
import { QuoteAPI } from "../utils/QuoteAPI";
import { PetUnderwriterType, QuoteStatus } from "@spotpetinsurance/spot-types/entities/PetQuote";
import { Quote } from "../types/Quote.interface";
import { Coverage } from "@spotpetinsurance/spot-types/entities/Coverage";
import { PublicConfig } from "../PublicConfig";
import Strings from "../utils/Strings.constants";
import { z } from "zod";

const PlanSchema = z.object({
    baseCoverage: z.any(),
    riders: z.array(z.any())
});

const PlansResponseSchema = z.array(PlanSchema);

export type PetPlan = { baseCoverage: Coverage; riders: Coverage[] };

// Filter amounts interface
export interface FilterAmounts {
    annualLimit?: number[];
    deductible?: number[];
    reimbursementRate?: number[];
}

// Shared interface for the hook return type
export interface PetPlansResult {
    petPlans: Record<string, PetPlan[]>;
    isPetPlansUpdating: boolean;
    isError: boolean;
    ratingZipcode?: string;
}

export interface UsePetPlansProps {
    quote: Quote | undefined;
    underwriter: PetUnderwriterType;
    isEnabled: boolean;
    priorityCode?: string;
    filterAmounts?: FilterAmounts; // NEW: Optional filter for coverage amounts
}

export const usePetPlans = ({ quote, underwriter, isEnabled, priorityCode, filterAmounts }: UsePetPlansProps): PetPlansResult => {
    const quoteApi = useMemo(() => new QuoteAPI(underwriter), [underwriter]);
    const { id: quoteId, policies = [], ratingZipcode } = quote || {};

    const disabledQuoteStatuses: QuoteStatus[] = ["finalized", "finalize-processing"];

    const queries = policies.map(policy => ({
        enabled: isEnabled && !!quote?.quoteStatus && !disabledQuoteStatuses.includes(quote.quoteStatus),
        staleTime: 1000 * 60 * 60,
        queryKey: ["petPlan", quoteId, ratingZipcode, policy.id, policy.species, policy.breedID, policy.gender, policy.age, priorityCode, filterAmounts],
        queryFn: async () => {
            if (!quoteId || !policy.id || !ratingZipcode) {
                throw new Error("Missing required parameters");
            }

            if (quote?.quoteStatus === "finalized") {
                throw new Error("Quote is already finalized");
            }

            try {
                const response = await quoteApi.getPlans(quoteId, policy.id, priorityCode);

                // Validate response is an array
                let plans: PetPlan[];
                try {
                    plans = PlansResponseSchema.parse(response) as PetPlan[];
                } catch (validationError) {
                    console.error("Invalid plans response:", {
                        validationError,
                        responseType: typeof response,
                        response
                    });
                    throw validationError;
                }

                let filteredPlans = plans;

                if (underwriter === "ptz-us") {
                    filteredPlans = plans
                        .map(p => {
                            if (p.baseCoverage.details?.annualLimit?.value === PublicConfig.UNLIMITED_ANNUAL_LIMIT_VALUE) {
                                return roundPlanPrices({
                                    ...p,
                                    baseCoverage: {
                                        ...p.baseCoverage,
                                        details: {
                                            ...p.baseCoverage.details,
                                            annualLimit: {
                                                symbol: Strings.UNLIMITED_ANNUAL_LIMIT_LABEL,
                                                value: PublicConfig.UNLIMITED_ANNUAL_LIMIT_VALUE
                                            }
                                        }
                                    }
                                });
                            }
                            return roundPlanPrices(p);
                        })
                        .filter(p => {
                            const annualLimitValue = p.baseCoverage.details?.annualLimit?.value;
                            return typeof annualLimitValue === "number" && (annualLimitValue <= 10000 || annualLimitValue === 0);
                        })
                        .filter(p => {
                            const deductible = p.baseCoverage.details?.deductible?.value;
                            return typeof deductible === "number" && deductible !== 300;
                        });
                } else {
                    filteredPlans = plans;
                }

                // Apply filterAmounts if provided
                if (filterAmounts) {
                    filteredPlans = filteredPlans.filter(plan => {
                        const details = plan.baseCoverage.details;

                        // Filter by annual limit
                        if (filterAmounts.annualLimit?.length) {
                            const annualLimitValue = details?.annualLimit?.value;
                            if (typeof annualLimitValue === "number" && filterAmounts.annualLimit.includes(annualLimitValue)) {
                                return false; // Exclude this plan
                            }
                        }

                        // Filter by deductible
                        if (filterAmounts.deductible?.length) {
                            const deductibleValue = details?.deductible?.value;
                            if (typeof deductibleValue === "number" && filterAmounts.deductible.includes(deductibleValue)) {
                                return false; // Exclude this plan
                            }
                        }

                        // Filter by reimbursement rate
                        if (filterAmounts.reimbursementRate?.length) {
                            const reimbursementValue = details?.reimbursementPercent?.value;
                            if (typeof reimbursementValue === "number" && filterAmounts.reimbursementRate.includes(reimbursementValue)) {
                                return false; // Exclude this plan
                            }
                        }

                        return true; // Keep this plan
                    });
                }

                return filteredPlans;
            } catch (err) {
                console.error("Error fetching pet plans:", err);
                throw err;
            }
        }
    }));

    const queryResults = useQueries({
        queries,
        combine: results => {
            const { petPlans } = results.reduce(
                (acc, result, index) => {
                    const policy = policies[index];
                    if (policy && policy.id && result.data) {
                        // Add all plans to petPlans
                        acc.petPlans[policy.id] = result.data;
                    }
                    return acc;
                },
                {
                    petPlans: {} as Record<string, PetPlan[]>
                }
            );

            return {
                petPlans,
                isPetPlansUpdating: results.some(result => result.isFetching || result.isLoading),
                isError: results.some(result => result.isError),
                ratingZipcode
            };
        }
    });

    return queryResults;
};

function roundDollarAmount(value: number | undefined): number {
    if (typeof value !== "number" || !Number.isFinite(value)) return 0;
    return Math.round(value * 100) / 100;
}

function roundPlanPrices(plan: PetPlan): PetPlan {
    return {
        ...plan,
        baseCoverage: {
            ...plan.baseCoverage,
            basePrice: {
                symbol: plan.baseCoverage.basePrice?.symbol ?? "$",
                value: roundDollarAmount(plan.baseCoverage.basePrice?.value)
            },
            discountAmount: {
                symbol: plan.baseCoverage.discountAmount?.symbol ?? "$",
                value: roundDollarAmount(plan.baseCoverage.discountAmount?.value)
            },
            taxAmount: {
                symbol: plan.baseCoverage.taxAmount?.symbol ?? "$",
                value: roundDollarAmount(plan.baseCoverage.taxAmount?.value)
            }
        },
        riders: plan.riders.map(rider => ({
            ...rider,
            basePrice: {
                symbol: rider.basePrice?.symbol ?? "$",
                value: roundDollarAmount(rider.basePrice?.value)
            },
            discountAmount: {
                symbol: rider.discountAmount?.symbol ?? "$",
                value: roundDollarAmount(rider.discountAmount?.value)
            },
            taxAmount: {
                symbol: rider.taxAmount?.symbol ?? "$",
                value: roundDollarAmount(rider.taxAmount?.value)
            }
        }))
    };
}
